<template>
  <div class="content-tile-page">

    <!-- Header -->
    <div class="page-header">
      <div>
            <h1>{{ bonusOnly ? 'Course Bonuses' : 'Content Tiles' }}</h1>
            <p>{{ bonusOnly ? 'Manage shared course bonus resources and their PDF download links.' : 'See which website area each tile belongs to through its linked main content.' }}</p>
      </div>

      <button class="primary-btn" @click="openCreate">
        <i class="bx bx-plus"></i>
        {{ bonusOnly ? 'Add Bonus' : 'Add Tile' }}
      </button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">

      <div class="stat-card">
        <div class="stat-icon blue">
          <i class="bx bx-grid-alt"></i>
        </div>

        <div>
          <span>{{ bonusOnly ? 'Bonus Resources' : 'Total Tiles' }}</span>
          <strong>{{ bonusOnly ? courseBonusStats.totalTiles : totalTiles }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green">
          <i class="bx bx-layout"></i>
        </div>

        <div>
          <span>{{ bonusOnly ? 'PDF Links' : 'Main Contents' }}</span>
          <strong>{{ bonusOnly ? courseBonusStats.downloadLinks : totalParents }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon purple">
          <i class="bx bx-category"></i>
        </div>

        <div>
          <span>{{ bonusOnly ? 'Missing PDF Links' : 'Categories' }}</span>
          <strong>{{ bonusOnly ? Math.max(courseBonusStats.totalTiles - courseBonusStats.downloadLinks, 0) : totalCategories }}</strong>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange">
          <i class="bx bx-calendar"></i>
        </div>

        <div>
          <span>{{ bonusOnly ? 'Shared Blocks' : 'This Month' }}</span>
          <strong>{{ bonusOnly ? courseBonusStats.totalParents : thisMonth }}</strong>
        </div>
      </div>

    </div>

    <!-- Main Card -->
    <div class="content-card">

      <!-- Toolbar -->
      <div class="toolbar">

        <div class="search-box">
          <i class="bx bx-search"></i>

          <input
            v-model="search"
            type="text"
            placeholder="Search tiles..."
            @keyup.enter="applyFilters"
          />
        </div>

        <!-- Dynamic Main Content -->
        <select
          v-model="parentFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Main Content</option>

          <option
            v-for="content in parents"
            :key="content.id"
            :value="content.id"
          >
            {{ content.title }} — {{ formatAreaLabel(content.parentType) }}
          </option>
        </select>

        <select
          v-if="!bonusOnly"
          v-model="parentTypeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Website Areas</option>

          <option
            v-for="parentType in parentTypes"
            :key="parentType"
            :value="parentType"
          >
            {{ formatAreaLabel(parentType) }}
          </option>
        </select>

        <select
          v-if="!bonusOnly"
          v-model="contentTypeFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Content Types</option>
          <option value="course_bonuses">Course Bonuses</option>
        </select>

        <select
          v-if="bonusOnly"
          v-model="downloadStatusFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All PDF Statuses</option>
          <option value="has-url">PDF link added</option>
          <option value="missing-url">PDF link missing</option>
        </select>

        <!-- Dynamic Category -->
        <select
          v-model="categoryFilter"
          class="filter-select"
          @change="applyFilters"
        >
          <option value="">All Categories</option>

          <option
            v-for="category in categories"
            :key="category.id"
            :value="category.id"
          >
            {{ category.name }}
          </option>
        </select>

        <button
          class="reset-btn"
          @click="resetFilters"
        >
          <i class="bx bx-reset"></i>
          Reset
        </button>

      </div>

      <!-- Table -->
      <div class="table-wrapper">
        <table>

          <thead>
            <tr>
              <th>Tile</th>
              <th>Website Area</th>
              <th>Main Content Section</th>
              <th>Category</th>
              <th>Download URL</th>
              <th>Icon</th>
              <th>Description</th>
              <th>Created</th>
              <th class="action-column">Actions</th>
            </tr>
          </thead>

          <tbody>

            <!-- Loading -->
            <tr v-if="loading">
              <td
                colspan="9"
                class="empty-state"
              >
                <i class="bx bx-loader-alt bx-spin"></i>
                <h3>Loading content tiles...</h3>
              </td>
            </tr>

            <!-- Rows -->
            <tr
              v-for="tile in tiles"
              v-else
              :key="tile.con_tile_id"
            >

              <!-- Tile -->
              <td>
                <div class="tile-info">

                  <div class="tile-image">

                    <img
                      v-if="tile.con_tile_img"
                      :src="tile.con_tile_img"
                      :alt="tile.con_tile_title"
                    />

                    <i
                      v-else
                      class="bx bx-grid-alt"
                    ></i>

                  </div>

                  <div>
                    <strong>
                      {{ tile.con_tile_title || "Untitled Tile" }}
                    </strong>

                    <small>
                      ID: #{{ tile.con_tile_id }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Website Area -->
              <td>
                <span
                  v-if="tile.con_parent_type"
                  class="area-badge"
                >
                  {{ formatAreaLabel(tile.con_parent_type) }}
                </span>
                <span v-else class="muted">Unassigned</span>
              </td>

              <!-- Main Content Section -->
              <td>
                <div class="parent-info">

                  <strong>
                    {{ tile.parent_title || getParentTitle(tile.con_tile_parent) || "—" }}
                  </strong>

                  <small v-if="tile.con_tile_parent">
                    Content #{{ tile.con_tile_parent }}
                  </small>

                  <span
                    v-else
                    class="muted"
                  >
                    No Parent
                  </span>

                  <small v-if="tile.con_type">
                    Content type: {{ formatLabel(tile.con_type) }}
                  </small>

                </div>
              </td>

              <!-- Category -->
              <td>

                <span
                  v-if="tile.category_name"
                  class="category-badge"
                >
                  {{ tile.category_name }}
                </span>

                <span
                  v-else
                  class="muted"
                >
                  {{ getCategoryName(tile.con_tile_cat) || "—" }}
                </span>

              </td>

              <td>
                <a
                  v-if="tile.con_tile_download_url"
                  :href="tile.con_tile_download_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="download-url-link"
                  :title="tile.con_tile_download_url"
                >
                  Open URL
                </a>
                <span v-else class="muted">Not set</span>
              </td>

              <!-- Icon -->
              <td>

                <div class="icon-preview">

                  <span v-if="tile.con_tile_icon">
                    {{ tile.con_tile_icon }}
                  </span>

                  <span
                    v-else
                    class="muted"
                  >
                    —
                  </span>

                </div>

              </td>

              <!-- Description -->
              <td>

                <div class="description-cell">
                  {{ tile.con_tile_desc || "—" }}
                </div>

              </td>

              <!-- Created -->
              <td>

                <span class="date-text">
                  {{ formatDate(tile.con_tile_datetime) }}
                </span>

              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="openView(tile)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="openEdit(tile)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="deleteTile(tile)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

            <!-- Empty -->
            <tr
              v-if="!loading && tiles.length === 0"
            >
              <td
                colspan="9"
                class="empty-state"
              >

                <i class="bx bx-grid-alt"></i>

                <h3>
                  No content tiles found
                </h3>

                <p>
                  Try changing your search or filters.
                </p>

              </td>
            </tr>

          </tbody>

        </table>
      </div>

      <!-- Pagination -->
      <div
        class="pagination"
        v-if="totalPages > 1"
      >

        <div class="pagination-info">

          Showing page

          <strong>
            {{ page }}
          </strong>

          of

          <strong>
            {{ totalPages }}
          </strong>

        </div>

        <div class="pagination-buttons">

          <button
            :disabled="page === 1 || loading"
            @click="changePage(page - 1)"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="pageNumber in totalPages"
            :key="pageNumber"
            :class="{ active: page === pageNumber }"
            @click="changePage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <button
            :disabled="page === totalPages || loading"
            @click="changePage(page + 1)"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit / View Modal -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @click.self="closeModal"
    >

      <div class="modal">

        <!-- Header -->
        <div class="modal-header">

          <div>

            <h2>
              {{
                modalMode === "create"
                  ? "Add Content Tile"
                  : modalMode === "edit"
                    ? "Edit Content Tile"
                    : "Content Tile Details"
              }}
            </h2>

            <p>
              {{
                modalMode === "create"
                  ? "Create a new content tile."
                  : modalMode === "edit"
                    ? "Update content tile details."
                    : "View content tile information."
              }}
            </p>

          </div>

          <button
            class="close-btn"
            @click="closeModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Body -->
        <div class="modal-body">

          <div class="form-grid">

            <!-- Main Content -->
            <div class="form-group full">

              <label>
                Main Content
              </label>

              <select
                v-model="form.con_tile_parent"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Main Content
                </option>

                <option
                  v-for="content in parents"
                  :key="content.id"
                  :value="content.id"
                >
                  {{ content.title }} — {{ formatAreaLabel(content.parentType) }}
                </option>

              </select>

              <small class="field-help">
                Select the main content for this tile.
              </small>

            </div>

            <!-- Tile Title -->
            <div class="form-group full">

              <label>
                Tile Title
              </label>

              <input
                v-model="form.con_tile_title"
                type="text"
                placeholder="Enter tile title"
                :disabled="isViewMode"
              />

            </div>

            <!-- Icon -->
            <div class="form-group">

              <label>
                Tile Icon
              </label>

              <input
                v-model="form.con_tile_icon"
                type="text"
                placeholder="e.g. bx-book-open"
                :disabled="isViewMode"
              />

            </div>

            <!-- Category -->
            <div
              class="form-group"
              v-if="modalMode !== 'create'"
            >

              <label>
                Category
              </label>

              <select
                v-model="form.con_tile_cat"
                :disabled="isViewMode"
              >

                <option :value="null">
                  Select Category
                </option>

                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>

              </select>

            </div>

            <!-- Image URL -->
            <div
              class="form-group full"
              v-if="modalMode !== 'create'"
            >

              <label>
                Tile Image URL
              </label>

              <input
                v-model="form.con_tile_img"
                type="text"
                placeholder="Enter image URL"
                :disabled="isViewMode"
              />

            </div>

            <div class="form-group full">
              <label>Download URL</label>
              <input
                v-model="form.con_tile_download_url"
                type="url"
                placeholder="https://example.com/resource.pdf"
                :disabled="isViewMode"
              />
            </div>

            <div class="form-group">
              <label>Theme Color</label>
              <input
                v-model="form.con_tile_theme_color"
                type="color"
                :disabled="isViewMode"
              />
            </div>

            <!-- Upload -->
            <div
              class="form-group full"
              v-if="!isViewMode"
            >

              <label>
                Upload Tile Image
              </label>

              <input
                type="file"
                accept="image/*"
                @change="handleImageChange"
              />

            </div>

            <!-- Image Preview -->
            <div
              class="form-group full"
              v-if="imagePreview"
            >

              <label>
                Image Preview
              </label>

              <div class="image-preview">

                <img
                  :src="imagePreview"
                  alt="Tile Preview"
                />

              </div>

            </div>

            <!-- Meta Title -->
            <div class="form-group full">

              <label>
                Meta Title
              </label>

              <input
                v-model="form.con_tile_meta_title"
                type="text"
                placeholder="Enter meta title"
                :disabled="isViewMode"
              />

            </div>

            <!-- Keywords -->
            <div class="form-group full">

              <label>
                Keywords
              </label>

              <textarea
                v-model="form.con_tile_keyword"
                rows="3"
                placeholder="Enter keywords"
                :disabled="isViewMode"
              ></textarea>

            </div>

            <!-- Description -->
            <div class="form-group full">

              <label>
                Description
              </label>

              <textarea
                v-model="form.con_tile_desc"
                rows="6"
                placeholder="Enter tile description"
                :disabled="isViewMode"
              ></textarea>

            </div>

            <!-- View ID -->
            <div
              class="form-group full"
              v-if="isViewMode && form.con_tile_id"
            >

              <small class="field-help">
                Tile ID:
                <strong>
                  #{{ form.con_tile_id }}
                </strong>
              </small>

            </div>

          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeModal"
          >
            {{ isViewMode ? "Close" : "Cancel" }}
          </button>

          <!-- View Mode -->
          <button
            v-if="isViewMode"
            class="primary-btn"
            @click="editFromView"
          >
            <i class="bx bx-edit"></i>
            Edit Tile
          </button>

          <!-- Create / Edit -->
          <button
            v-else
            class="primary-btn"
            :disabled="saving"
            @click="saveTile"
          >

            <i
              class="bx"
              :class="
                saving
                  ? 'bx-loader-alt bx-spin'
                  : 'bx-save'
              "
            ></i>

            {{
              saving
                ? "Saving..."
                : modalMode === "edit"
                  ? "Update Tile"
                  : "Save Tile"
            }}

          </button>

        </div>

      </div>

    </div>

  </div>
</template>

<script>
import {
  getContentTilesApi,
  getContentTileStatsApi,
  getContentTileApi,
  createContentTileApi,
  updateContentTileApi,
  deleteContentTileApi
} from "@/apis/contenttileapis";

import {
  getCategoriesApi
} from "@/apis/categoryapis";

import {
  getContentMainsApi
} from "@/apis/contentmainapis";

export default {
  name: "ContentTiles",

  props: {
    bonusOnly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      saving: false,

      tiles: [],

      // Dynamic Main Content
      parents: [],
      parentTypes: [],

      // Dynamic Categories
      categories: [],

      totalTiles: 0,
      totalParents: 0,
      totalCategories: 0,
      thisMonth: 0,

      page: 1,
      limit: this.bonusOnly ? 100 : 10,
      totalPages: 1,

      search: "",
      parentFilter: "",
      parentTypeFilter: this.bonusOnly ? "course" : "",
      contentTypeFilter: this.bonusOnly ? "course_bonuses" : "",
      downloadStatusFilter: "",
      categoryFilter: "",
      courseBonusStats: {
        totalTiles: 0,
        totalParents: 0,
        downloadLinks: 0
      },

      showModal: false,
      modalMode: "view",

      form: {
        con_tile_id: null,
        con_tile_parent: null,
        con_tile_icon: "",
        con_tile_title: "",
        con_tile_meta_title: "",
        con_tile_keyword: "",
        con_tile_desc: "",
        con_tile_img: "",
        con_tile_download_url: "",
        con_tile_theme_color: "#0f172a",
        con_tile_cat: null
      },

      selectedImage: null,
      imagePreview: ""
    };
  },

  computed: {
    isViewMode() {
      return this.modalMode === "view";
    },

    modalTitle() {
      if (this.modalMode === "create") {
        return "Add Tile";
      }

      if (this.modalMode === "edit") {
        return "Edit Tile";
      }

      return "Tile Details";
    }
  },

  mounted() {
    this.loadStats();
    this.loadMainContents();
    this.loadCategories();
    this.loadTiles();
  },

  watch: {
    bonusOnly(enabled) {
      this.limit = enabled ? 100 : 10;
      this.parentTypeFilter = enabled ? "course" : "";
      this.contentTypeFilter = enabled ? "course_bonuses" : "";
      this.downloadStatusFilter = "";
      this.parentFilter = "";
      this.search = "";
      this.page = 1;
      this.loadStats();
      this.loadTiles();
    }
  },

  methods: {

    /*
     * -----------------------------------------
     * LOAD STATS
     * -----------------------------------------
     */
    async loadStats() {
      try {
        const res = await getContentTileStatsApi(
          this.bonusOnly
            ? { parentType: "course", contentType: "course_bonuses" }
            : {},
        );

        if (res?.status || res?.success) {
          const data = res.data || res;

          if (this.bonusOnly) {
            this.courseBonusStats = {
              totalTiles: Number(data.totalTiles || 0),
              totalParents: Number(data.totalParents || 0),
              downloadLinks: Number(data.downloadLinks || 0)
            };
            return;
          }

          this.totalTiles =
            data.totalTiles ??
            data.total_tiles ??
            data.total ??
            0;

          this.totalParents =
            data.totalParents ??
            data.mainContents ??
            data.parents ??
            0;

          this.totalCategories =
            data.totalCategories ??
            data.categories ??
            0;

          this.thisMonth =
            data.thisMonth ??
            data.this_month ??
            0;
        }
      } catch (error) {
        console.error(
          "Content tile stats error:",
          error
        );
      }
    },

    /*
     * -----------------------------------------
     * LOAD MAIN CONTENTS FROM API
     * -----------------------------------------
     */
    async loadMainContents() {
      try {
        const res = await getContentMainsApi({
          page: 1,
          limit: 100,
          search: ""
        });

        if (res?.status === false) {
          this.parents = [];
          return;
        }

        const data = res?.data || res || {};

        const contents =
          data.contents ||
          data.contentMain ||
          data.contentMains ||
          data.rows ||
          data.mainContents ||
          [];

        this.parents = Array.isArray(contents)
          ? contents.map((item) => ({
              id:
                item.id ??
                item.con_id ??
                item.con_main_id ??
                item.con_main_content_id,

              title:
                item.title ??
                item.con_title ??
                item.con_main_title ??
                "Untitled Content",

              parentType:
                item.con_parent_type ??
                item.parentType ??
                ""
            }))
          : [];

      } catch (error) {
        console.error(
          "Load Main Contents Error:",
          error
        );

        this.parents = [];
      }
    },

    /*
     * -----------------------------------------
     * LOAD CATEGORIES
     * cat_type = content
     * -----------------------------------------
     */
    async loadCategories() {
      try {
        const res = await getCategoriesApi({
          page: 1,
          limit: 100,
          search: "",
          cat_type: "content"
        });

        if (res?.status === false) {
          this.categories = [];
          return;
        }

        const data = res?.data || res || {};

        const categoryList =
          Array.isArray(data)
            ? data
            : data.categories ||
              data.rows ||
              data.data ||
              [];

        this.categories = Array.isArray(categoryList)
          ? categoryList.map((item) => ({
              id:
                item.id ??
                item.cat_id ??
                item.category_id,

              name:
                item.name ??
                item.cat_name ??
                item.category_name ??
                "Unnamed Category"
            }))
          : [];

      } catch (error) {
        console.error(
          "Load Content Categories Error:",
          error
        );

        this.categories = [];
      }
    },

    /*
     * -----------------------------------------
     * LOAD TILES
     * -----------------------------------------
     */
    async loadTiles() {
      this.loading = true;

      try {
        const res = await getContentTilesApi({
          page: this.page,
          limit: this.limit,
          search: this.search,
          parent: this.parentFilter,
          parentType: this.parentTypeFilter,
          contentType: this.contentTypeFilter,
          category: this.categoryFilter,
          downloadStatus: this.downloadStatusFilter
        });

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.tiles =
            data.tiles ||
            data.contentTiles ||
            data.rows ||
            [];

          this.totalTiles =
            data.total ??
            data.totalTiles ??
            this.tiles.length;

          this.totalPages =
            data.totalPages ??
            data.total_pages ??
            Math.ceil(
              this.totalTiles / this.limit
            ) ?? 1;

          /*
           * Only use parents/categories returned
           * by tile API if separate API did not
           * already populate them.
           */
          if (
            this.parents.length === 0 &&
            Array.isArray(data.parents)
          ) {
            this.parents = data.parents.map((item) => ({
              id: item.id ?? item.con_id,
              title: item.title ?? item.con_title ?? "Untitled Content",
              parentType: item.parentType ?? item.con_parent_type ?? ""
            }));
          }

          if (
            this.categories.length === 0 &&
            Array.isArray(data.categories)
          ) {
            this.categories = data.categories;
          }

          this.parentTypes = data.parentTypes || this.parentTypes;

        } else {
          this.tiles = [];
          this.totalTiles = 0;
          this.totalPages = 1;
        }

      } catch (error) {
        console.error(
          "Content tile list error:",
          error
        );

        this.tiles = [];
        this.totalTiles = 0;
        this.totalPages = 1;

      } finally {
        this.loading = false;
      }
    },

    /*
     * -----------------------------------------
     * FILTERS
     * -----------------------------------------
     */
    applyFilters() {
      this.page = 1;
      this.loadTiles();
    },

    resetFilters() {
      this.search = "";
      this.parentFilter = "";
      this.parentTypeFilter = this.bonusOnly ? "course" : "";
      this.contentTypeFilter = this.bonusOnly ? "course_bonuses" : "";
      this.downloadStatusFilter = "";
      this.categoryFilter = "";
      this.page = 1;

      this.loadTiles();
    },

    /*
     * -----------------------------------------
     * PAGINATION
     * -----------------------------------------
     */
    changePage(page) {
      if (
        page < 1 ||
        page > this.totalPages
      ) {
        return;
      }

      this.page = page;
      this.loadTiles();
    },

    /*
     * -----------------------------------------
     * CREATE
     * -----------------------------------------
     */
    openCreate() {
      this.resetForm();

      this.modalMode = "create";
      this.showModal = true;
    },

    /*
     * -----------------------------------------
     * VIEW
     * -----------------------------------------
     */
    async openView(tile) {
      if (!tile?.con_tile_id) {
        return;
      }

      await this.loadTileDetails(
        tile.con_tile_id,
        "view"
      );
    },

    /*
     * -----------------------------------------
     * EDIT
     * -----------------------------------------
     */
    async openEdit(tile) {
      if (!tile?.con_tile_id) {
        return;
      }

      await this.loadTileDetails(
        tile.con_tile_id,
        "edit"
      );
    },

    /*
     * -----------------------------------------
     * LOAD TILE DETAILS
     * -----------------------------------------
     */
    async loadTileDetails(tileId, mode) {
      this.loading = true;

      try {
        const res =
          await getContentTileApi(tileId);

        if (res?.status || res?.success) {
          const data = res.data || res;

          this.form = {
            con_tile_id:
              data.con_tile_id ??
              tileId,

            con_tile_parent:
              data.con_tile_parent !== null &&
              data.con_tile_parent !== undefined &&
              data.con_tile_parent !== ""
                ? Number(data.con_tile_parent)
                : null,

            con_tile_icon:
              data.con_tile_icon ?? "",

            con_tile_title:
              data.con_tile_title ?? "",

            con_tile_meta_title:
              data.con_tile_meta_title ?? "",

            con_tile_keyword:
              data.con_tile_keyword ?? "",

            con_tile_desc:
              data.con_tile_desc ?? "",

            con_tile_img:
              data.con_tile_img ?? "",

            con_tile_download_url:
              data.con_tile_download_url ?? "",

            con_tile_theme_color:
              data.con_tile_theme_color || "#0f172a",

            con_tile_cat:
              data.con_tile_cat !== null &&
              data.con_tile_cat !== undefined &&
              data.con_tile_cat !== ""
                ? Number(data.con_tile_cat)
                : null
          };

          this.imagePreview =
            data.con_tile_img || "";

          this.selectedImage = null;

          this.modalMode = mode;
          this.showModal = true;
        }

      } catch (error) {
        console.error(
          "Content tile details error:",
          error
        );

      } finally {
        this.loading = false;
      }
    },

    /*
     * -----------------------------------------
     * IMAGE CHANGE
     * -----------------------------------------
     */
    handleImageChange(event) {
      const file =
        event.target.files?.[0];

      if (!file) {
        return;
      }

      this.selectedImage = file;

      this.imagePreview =
        URL.createObjectURL(file);
    },

    /*
     * -----------------------------------------
     * SAVE / UPDATE
     * -----------------------------------------
     */
    async saveTile() {
  if (!this.form.con_tile_title || !this.form.con_tile_title.trim()) {
    alert("Tile title is required.");
    return;
  }

  if (
    this.form.con_tile_parent === null ||
    this.form.con_tile_parent === "" ||
    this.form.con_tile_parent === undefined
  ) {
    alert("Please select Main Content.");
    return;
  }

  this.saving = true;

  try {
    const payload = {
      con_tile_id:
        this.form.con_tile_id || null,

      con_tile_parent:
        this.form.con_tile_parent !== null &&
        this.form.con_tile_parent !== ""
          ? Number(this.form.con_tile_parent)
          : null,

      con_tile_icon:
        this.form.con_tile_icon || "",

      con_tile_title:
        this.form.con_tile_title.trim(),

      con_tile_meta_title:
        this.form.con_tile_meta_title || "",

      con_tile_keyword:
        this.form.con_tile_keyword || "",

      con_tile_desc:
        this.form.con_tile_desc || "",

      con_tile_img:
        this.form.con_tile_img || "",

      con_tile_download_url:
        this.form.con_tile_download_url || "",

      con_tile_theme_color:
        this.form.con_tile_theme_color || "#0f172a",

      con_tile_cat:
        this.form.con_tile_cat !== null &&
        this.form.con_tile_cat !== ""
          ? Number(this.form.con_tile_cat)
          : null
    };

    if (this.selectedImage) {
      payload.con_tile_img = this.selectedImage;
    }

    console.log("Content Tile Save Payload:", payload);

    let res;

    if (this.modalMode === "edit") {
      res = await updateContentTileApi(payload);
    } else {
      res = await createContentTileApi(payload);
    }

    console.log("Content Tile Save Response:", res);

    if (res?.status === true || res?.success === true) {
      alert(
        res?.success ||
        (
          this.modalMode === "edit"
            ? "Tile updated successfully."
            : "Tile created successfully."
        )
      );

      this.closeModal();

      await this.loadStats();
      await this.loadTiles();

      return;
    }

    alert(
      res?.success ||
      res?.message ||
      "Unable to save content tile."
    );

  } catch (error) {
    console.error("Content tile save error:", error);

    alert(
      error?.response?.data?.success ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to save content tile."
    );

  } finally {
    this.saving = false;
  }
},

    /*
     * -----------------------------------------
     * DELETE
     * -----------------------------------------
     */
    async deleteTile(tile) {
      if (!tile?.con_tile_id) {
        return;
      }

      const confirmed = confirm(
        `Are you sure you want to delete "${tile.con_tile_title || "this tile"}"?`
      );

      if (!confirmed) {
        return;
      }

      try {
        const res =
          await deleteContentTileApi(
            tile.con_tile_id
          );

        if (res?.status || res?.success) {
          await this.loadStats();

          if (
            this.tiles.length === 1 &&
            this.page > 1
          ) {
            this.page--;
          }

          await this.loadTiles();
        }

      } catch (error) {
        console.error(
          "Content tile delete error:",
          error
        );
      }
    },

    /*
     * -----------------------------------------
     * RESET FORM
     * -----------------------------------------
     */
    resetForm() {
      this.form = {
        con_tile_id: null,
        con_tile_parent: null,
        con_tile_icon: "",
        con_tile_title: "",
        con_tile_meta_title: "",
        con_tile_keyword: "",
        con_tile_desc: "",
        con_tile_img: "",
        con_tile_download_url: "",
        con_tile_theme_color: "#0f172a",
        con_tile_cat: null
      };

      this.selectedImage = null;
      this.imagePreview = "";
    },

    /*
     * -----------------------------------------
     * CLOSE MODAL
     * -----------------------------------------
     */
    closeModal() {
      this.showModal = false;
      this.modalMode = "view";

      this.resetForm();
    },

    /*
     * -----------------------------------------
     * VIEW -> EDIT
     * -----------------------------------------
     */
    editFromView() {
      this.modalMode = "edit";
    },

    /*
     * -----------------------------------------
     * GET MAIN CONTENT TITLE
     * -----------------------------------------
     */
    getParentTitle(parentId) {
      if (
        parentId === null ||
        parentId === undefined ||
        parentId === ""
      ) {
        return "";
      }

      const parent =
        this.parents.find(
          (item) =>
            Number(item.id) ===
            Number(parentId)
        );

      return parent?.title || "";
    },

    /*
     * -----------------------------------------
     * GET CATEGORY NAME
     * -----------------------------------------
     */
    getCategoryName(categoryId) {
      if (
        categoryId === null ||
        categoryId === undefined ||
        categoryId === ""
      ) {
        return "";
      }

      const category =
        this.categories.find(
          (item) =>
            Number(item.id) ===
            Number(categoryId)
        );

      return category?.name || "";
    },

    /*
     * -----------------------------------------
     * DATE FORMAT
     * -----------------------------------------
     */
    formatDate(date) {
      if (!date) {
        return "—";
      }

      const parsedDate =
        new Date(date);

      if (
        Number.isNaN(
          parsedDate.getTime()
        )
      ) {
        return date;
      }

      return parsedDate.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric"
        }
      );
    },

    formatLabel(value) {
      return String(value || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    formatAreaLabel(value) {
      const labels = {
        home: "Home page",
        event: "Events",
        course: "Course page",
        creator: "Creator page",
        blog: "Blog page"
      };

      return labels[value] || this.formatLabel(value) || "Unassigned";
    }
  }
};
</script>
<style scoped>
.content-tile-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.reset-btn,
.danger-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s ease;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn,
.reset-btn {
  background: #fff;
  border-color: #e2e8f0;
  color: #475569;
}

.secondary-btn:hover,
.reset-btn:hover {
  background: #f8fafc;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
}

.stat-icon.blue {
  background: #eff6ff;
  color: #2563eb;
}

.stat-icon.green {
  background: #f0fdf4;
  color: #16a34a;
}

.stat-icon.purple {
  background: #faf5ff;
  color: #9333ea;
}

.stat-icon.orange {
  background: #fff7ed;
  color: #ea580c;
}

.stat-card span {
  display: block;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-card strong {
  display: block;
  font-size: 22px;
  line-height: 1;
}

/* Card */
.content-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

/* Toolbar */
.toolbar {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 360px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-select {
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  outline: none;
  font-size: 14px;
  color: #334155;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-select {
  min-width: 165px;
  padding: 0 12px;
}

.search-box input:focus,
.filter-select:focus,
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: #2563eb;
}

/* Table */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1320px;
  border-collapse: collapse;
}

thead {
  background: #f8fafc;
}

th {
  padding: 13px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  vertical-align: middle;
}

tbody tr:hover {
  background: #fafcff;
}

.action-column {
  width: 125px;
  text-align: right;
}

/* Tile */
.tile-info {
  display: flex;
  align-items: center;
  gap: 11px;
}

.tile-image {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 9px;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
}

.tile-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tile-info strong {
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e293b;
  font-size: 13px;
}

.tile-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.parent-info strong {
  display: block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #334155;
  font-size: 13px;
}

.parent-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
}

.area-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 20px;
  background: #ecfdf5;
  color: #047857;
  font-size: 11px;
  font-weight: 600;
}

.icon-preview {
  font-size: 12px;
  color: #64748b;
}

.description-cell {
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #64748b;
}

.date-text {
  white-space: nowrap;
  color: #64748b;
}

.muted {
  color: #94a3b8;
}

/* Actions */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.icon-btn.view {
  color: #2563eb;
}

.icon-btn.edit {
  color: #64748b;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h3 {
  margin: 12px 0 5px;
  font-size: 16px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  padding: 14px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.pagination-info {
  color: #64748b;
  font-size: 12px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 7px;
  color: #475569;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.pagination-buttons button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.view-modal {
  max-width: 720px;
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.modal-header h2 {
  margin: 0 0 5px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  background: #f8fafc;
  border-radius: 7px;
  color: #64748b;
  cursor: pointer;
  font-size: 20px;
}

.modal-body {
  padding: 22px;
  overflow-y: auto;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 13px;
  color: #334155;
  outline: none;
  background: #fff;
}

.form-group input,
.form-group select {
  height: 40px;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.field-help {
  color: #94a3b8;
  font-size: 11px;
}

.field-help strong {
  color: #64748b;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.tile-detail-top {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.large-tile-image {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.large-tile-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tile-detail-top h3 {
  margin: 0 0 5px;
  color: #1e293b;
  font-size: 18px;
}

.id-label {
  color: #94a3b8;
  font-size: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item.full {
  grid-column: 1 / -1;
}

.detail-item label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.detail-item span,
.detail-item p {
  margin: 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.description-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 13px;
  color: #475569;
  line-height: 1.6;
  font-size: 13px;
}

/* Delete */
.delete-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}

.delete-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
}

.delete-modal h2 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.delete-actions {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* Responsive */
@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 700px) {
  .content-tile-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .toolbar {
    flex-wrap: wrap;
  }

  .search-box {
    width: 100%;
    max-width: none;
  }

  .filter-select {
    flex: 1;
  }

  .form-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full,
  .detail-item.full {
    grid-column: auto;
  }

  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
